<template>
    <MainContent
        title="Familias de abonados"
        :value="filters"
        :meta="familias.meta"
        @input="val => Object.assign(filters, val)"
        @updated="fetchFamilias"
    >
        <template #header>
            <Card customClasses="tw:mb-4">
                <div class="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                    <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:gap-3 tw:sm:gap-4 tw:md:gap-6">
                        <div class="tw:w-full">
                            <SearchComponent
                                v-model="filters.search"
                                placeholder="Buscar"
                                @input="filters.page = 1; fetchFamilias()"
                            />
                        </div>
                        <div class="tw:w-full tw:sm:w-48 tw:md:w-64">
                            <BaseMultiselect
                                v-model="filters.estado"
                                name="estado"
                                label="Estado"
                                :options="estadoOptions"
                                value-key="value"
                                label-key="name"
                                @input="onEstadoChange"
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </template>

        <div class="tw:bg-white tw:rounded-xl">
            <BaseTable
                :columns="columns"
                :data="familias.data || []"
                :loading="loading"
                :skeleton="loading"
                :skeleton-rows="5"
                empty-text="No hay familias"
                custom-class="tw:min-h-[200px]"
                :default-col-width="defaultColWidth"
            >
                <template #cell-nombre="{ row }">
                    <span class="tw:font-medium tw:text-gray-900">{{ row.nombre || 'Sin nombre' }}</span>
                </template>

                <template #cell-titular="{ row }">
                    <span>{{ row.titular?.name }} {{ row.titular?.surnames }}</span>
                </template>

                <template #cell-miembros_count="{ row }">
                    <span class="tw:text-sm">{{ row.miembros_count || 0 }}</span>
                </template>

                <template #cell-estado="{ row }">
                    <div class="tw:inline-flex tw:items-center tw:gap-1">
                        <span
                            class="tw:inline-flex tw:items-center tw:px-2.5 tw:py-0.5 tw:rounded-full tw:text-xs tw:font-medium"
                            :class="estadoBadgeClass(row.estado)"
                        >
                            {{ estadoLabel(row.estado) }}
                        </span>
                        <i
                            v-if="row.estado === 'rechazado' && row.motivo_rechazo"
                            v-b-tooltip.hover.html="revisionTooltip(row)"
                            class="material-icons tw:text-sm tw:text-gray-400 tw:cursor-help"
                        >info</i>
                    </div>
                </template>

                <template #cell-acciones="{ row }">
                    <OptionsDropdownComponent
                        :options="getRowOptions(row)"
                    />
                </template>
            </BaseTable>
        </div>

        <!-- Eliminar modal -->
        <BorrarModalComponent
            v-if="deleteFamiliaData"
            :name="'borrar-familia-' + deleteFamiliaData.id"
            title="Eliminar familia"
            @confirm="confirmDelete"
            @cancel="deleteFamiliaData = null"
            @close="deleteFamiliaData = null"
        >
            ¿Está seguro de que quiere eliminar la familia "{{ deleteFamiliaData.nombre || 'Sin nombre' }}"?
        </BorrarModalComponent>

        <!-- Motivo Dialog -->
        <MotivoDialog
            v-if="motivoDialog.open"
            :name="'motivo-rechazo-' + motivoDialog.familia?.id"
            :familia="motivoDialog.familia"
            :loading="rejectLoading"
            :error="motivoDialog.error"
            @confirm="handleRejectConfirm"
            @cancel="closeMotivoDialog"
        />

        <!-- Confirmar aprobación -->
        <ModalComponent
            v-if="approveData"
            :name="'aprobar-familia-' + approveData.id"
            title="Aprobar familia"
            @close="approveData = null"
        >
            <div class="tw:text-sm tw:text-gray-600 tw:space-y-2">
                <p>¿Está seguro de que quiere <strong>aprobar</strong> la familia <strong>{{ approveData.nombre || 'Sin nombre' }}</strong>?</p>
                <p>Al aprobarla, los miembros podrán acceder a los <strong>beneficios de precios para familias</strong>.</p>
            </div>
            <template #footer>
                <div class="tw:flex tw:justify-end tw:gap-2">
                    <Button variant="secondary" @click="approveData = null" :disabled="approveLoading">Cancelar</Button>
                    <Button variant="primary" :loading="approveLoading" @click="confirmApprove">Aprobar familia</Button>
                </div>
            </template>
        </ModalComponent>

        <!-- Editar nombre modal -->
        <RenameFamiliaModal
            v-if="editNombreData"
            :name="`editar-nombre-${editNombreData.id}-${instance.uid}`"
            :nombre="editNombreForm.nombre"
            :original="editNombreOriginal"
            :loading="editNombreLoading"
            :error="editNombreError"
            @update:nombre="editNombreForm.nombre = $event"
            @submit="confirmEditNombre"
            @close="editNombreData = null"
        />

        <!-- Confirmar rechazo (aviso de beneficios) — se muestra dentro del MotivoDialog así que no hace falta extra -->
    </MainContent>
</template>

<script setup>
    import { ref, reactive, onMounted, getCurrentInstance, nextTick, watch } from 'vue'

    defineProps({
        defaultColWidth: {
            type: String,
            default: '105px'
        }
    })
    import { useRouter, useRoute } from '@/composables/common/router/useRouter.js'
    import { useFamilias } from '@/composables/admin/abonados/useFamilias'
    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import Card from '@/modules/admin/ui/Card.vue'
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue'
    import ModalComponent from '@/modules/admin/ui/ModalComponent.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import MotivoDialog from '@/modules/admin/abonados/familias/components/MotivoDialog.vue'
    import RenameFamiliaModal from '@/modules/admin/abonados/familias/modals/RenameFamiliaModal.vue'
    import { estadoBadgeClass, estadoLabel } from '@/modules/admin/abonados/familias/utils.js'

    const router = useRouter()
    const route = useRoute()
    const instance = getCurrentInstance()
    const {
        familias,
        loading,
        filters,
        fetchFamilias,
        deleteFamilia,
        approveFamily,
        rejectFamily,
        updateNombre,
    } = useFamilias()

    // Aplicar filtro inicial desde query param ANTES del mount del template
    if (route?.query?.estado) {
        filters.value.estado = String(route.query.estado)
    }

    const deleteFamiliaData = ref(null)
    const approveData = ref(null)
    const approveLoading = ref(false)
    const rejectLoading = ref(false)
    const motivoDialog = ref({
        open: false,
        familia: null,
        error: null,
    })
    const editNombreData = ref(null)
    const editNombreForm = reactive({ nombre: '' })
    const editNombreOriginal = ref('')
    const editNombreLoading = ref(false)
    const editNombreError = ref(null)

    const columns = [
        { key: 'nombre', label: 'Familia', sortable: false, class: 'tw:w-40' },
        { key: 'titular', label: 'Titular', sortable: false, class: 'tw:w-56' },
        { key: 'miembros_count', label: 'Miembros', sortable: false, centered: true, },
        { key: 'estado', label: 'Estado', sortable: false, centered: true, class: 'tw:w-32'},
        { key: 'acciones', label: '', sortable: false, centered: true , class: 'tw:w-32'},
    ]

    const estadoOptions = [
        { name: 'Pendiente', value: 'pendiente' },
        { name: 'Aprobado', value: 'aprobado' },
        { name: 'Rechazado', value: 'rechazado' },
    ]

    watch(deleteFamiliaData, async val => {
        if (val) { await nextTick(); instance.proxy.$modal.show('borrar-familia-' + val.id) }
    })

    watch(approveData, async val => {
        if (val) { await nextTick(); instance.proxy.$modal.show('aprobar-familia-' + val.id) }
    })

    watch(() => motivoDialog.value.open, async val => {
        if (val && motivoDialog.value.familia) {
            await nextTick()
            instance.proxy.$modal.show('motivo-rechazo-' + motivoDialog.value.familia.id)
        }
    })

    function onEstadoChange(val) {
        filters.value.estado = val || ''
        filters.value.page = 1
        fetchFamilias()
        if (route.query.estado) {
            router.replace({ query: {} }).catch(() => {})
        }
    }

    function getRowOptions(row) {
        const options = []

        if (row.estado !== 'aprobado') {
            options.push({
                type: 'action',
                action: () => handleApprove(row),
                label: 'Aprobar',
                icon: 'check',
            })
        }

        options.push({
            type: 'action',
            action: () => router.push({ name: 'back.familias.show', params: { familia_id: row.id } }),
            label: 'Detalle',
            icon: 'eye',
        })

        options.push({
            type: 'action',
            action: () => openEditNombre(row),
            label: 'Editar',
            icon: 'edit',
        })

        if (row.estado !== 'rechazado') {
            options.push({
                type: 'action',
                action: () => openMotivoDialog(row),
                label: 'Rechazar',
                icon: 'times',
            })
        }

        options.push({
            type: 'action',
            action: () => { deleteFamiliaData.value = row },
            label: 'Eliminar',
            icon: 'trash',
        })

        return options
    }

    async function confirmDelete() {
        const row = deleteFamiliaData.value
        instance.proxy.$modal.hide('borrar-familia-' + row.id)
        deleteFamiliaData.value = null
        await deleteFamilia(row.id)
    }

    async function handleApprove(row) {
        approveData.value = row
    }

    async function confirmApprove() {
        const row = approveData.value
        approveLoading.value = true
        try {
            await approveFamily(row.id)
            instance.proxy.$modal.hide('aprobar-familia-' + row.id)
            approveData.value = null
        } catch (err) {
            // Toast ya lo maneja approveFamily
        } finally {
            approveLoading.value = false
        }
    }

    function openMotivoDialog(familia) {
        motivoDialog.value = { open: true, familia, error: null }
    }

    function closeMotivoDialog() {
        instance.proxy.$modal.hide('motivo-rechazo-' + motivoDialog.value.familia?.id)
        motivoDialog.value = { open: false, familia: null, error: null }
    }

    function openEditNombre(row) {
        editNombreData.value = row
        editNombreForm.nombre = row.nombre || ''
        editNombreOriginal.value = row.nombre || ''
        editNombreError.value = null
        nextTick(() => instance.proxy.$modal.show(`editar-nombre-${row.id}-${instance.uid}`))
    }

    async function confirmEditNombre(nombre) {
        const row = editNombreData.value
        if (!row || !nombre || !nombre.trim()) return
        editNombreLoading.value = true
        editNombreError.value = null
        try {
            await updateNombre(row.id, nombre.trim())
            instance.proxy.$modal.hide(`editar-nombre-${row.id}-${instance.uid}`)
            editNombreData.value = null
        } catch (err) {
            editNombreError.value = err?.response?.data?.error || err?.response?.data?.message || 'Error al actualizar el nombre'
        } finally {
            editNombreLoading.value = false
        }
    }

    async function handleRejectConfirm(motivo) {
        const familia = motivoDialog.value.familia
        if (!familia) return

        rejectLoading.value = true
        try {
            await rejectFamily(familia.id, motivo)
            instance.proxy.$modal.hide('motivo-rechazo-' + familia.id)
            motivoDialog.value = { open: false, familia: null, error: null }
        } catch (err) {
            motivoDialog.value.error = err?.response?.data?.error || err?.response?.data?.message || 'Error al rechazar la familia'
        } finally {
            rejectLoading.value = false
        }
    }

    function revisionTooltip(row) {
        const nombre = row.revisado_por?.name || '—'
        return `Motivo: ${row.motivo_rechazo}<br>Revisado por ${nombre} el ${row.revisado_en || '—'}`
    }

    watch(
        () => route.query.estado,
        newEstado => {
            if (newEstado && filters.value.estado !== String(newEstado)) {
                filters.value.estado = String(newEstado)
                fetchFamilias()
            }
        }
    )

    onMounted(() => {
        fetchFamilias()
    })
</script>
